import { createAction, handleActions, Reducer } from 'redux-actions';
import { filter, includes, countBy } from 'lodash';
import { AdvancedUser, User } from '@adjust/dashboard-core/api/types';
import { DisplayRolesCount } from '@api/types';
import { SelectorState, Trace, TraceStatus } from '@redux/types';

type UserReducer<T = UsersState> = Reducer<UsersState, T>;

export interface UsersState {
  isLoading: boolean;
  trace: Nullable<Trace>;
  searchTerm: string;
  list: User[];
  advancedList: AdvancedUser[];
  toggleId: Nullable<number>;
  loadingUserId: Nullable<number>;
}

export const initialState: UsersState = {
  isLoading: false,
  trace: null,
  searchTerm: '',
  list: [],
  advancedList: [],
  toggleId: null,
  loadingUserId: null,
};

// Actions

export const FETCH_USERS_STARTED = 'users/FETCH_USERS_STARTED';
export const FETCH_USERS_SUCCEEDED = 'users/FETCH_USERS_SUCCEEDED';
export const FETCH_USERS_FAILED = 'users/FETCH_USERS_FAILED';

export const SET_SEARCH_TERM = 'users/SET_SEARCH_TERM';

export const CREATE_USER = 'users/CREATE_USER';
export const DELETE_USER = 'users/DELETE_USER';
export const EDIT_USER = 'users/EDIT_USER';
export const SET_LIST = 'users/SET_LIST';

export const TOGGLE_USER = 'users/TOGGLE_USER';
export const TOGGLE_USER_SUCCEEDED = 'users/TOGGLE_USER_SUCCEEDED';

export const SET_MAIN_CONTACT = 'users/SET_MAIN_CONTACT';
export const SET_MAIN_CONTACT_SUCCEEDED = 'users/SET_MAIN_CONTACT_SUCCEEDED';
export const SET_MAIN_CONTACT_FAILED = 'users/SET_MAIN_CONTACT_FAILED';

// Payloads

type FetchUserSucceededPayload = Pick<UsersState, 'list' | 'advancedList'>;
type FetchUserFailedPayload = string;
type SetSearchTermPayload = string;
type SetListPayload = Pick<UsersState, 'list'>;
type ToggleUserSucceededPayload = Nullable<number>;
type SetMainContactPayload = number;

// Action creators

export const fetchUsers = createAction(FETCH_USERS_STARTED);
export const fetchUsersSucceeded = createAction<FetchUserSucceededPayload>(FETCH_USERS_SUCCEEDED);
export const fetchUsersFailed = createAction<FetchUserFailedPayload>(FETCH_USERS_FAILED);

export const setSearchTerm = createAction<SetSearchTermPayload>(SET_SEARCH_TERM);

export const createUser = createAction(CREATE_USER);
export const deleteUser = createAction(DELETE_USER);
export const editUser = createAction(EDIT_USER);
export const setList = createAction<SetListPayload>(SET_LIST);

export const toggleUser = createAction(TOGGLE_USER);
export const toggleUserSucceeded = createAction<ToggleUserSucceededPayload>(TOGGLE_USER_SUCCEEDED);

export const setMainContact = createAction<SetMainContactPayload>(SET_MAIN_CONTACT);
export const setMainContactSucceeded = createAction(SET_MAIN_CONTACT_SUCCEEDED);
export const setMainContactFailed = createAction(SET_MAIN_CONTACT_FAILED);

// Reducers

const handleFetchUsers: UserReducer = state => ({
  ...state,
  isLoading: true,
});

const handleFetchUsersSucceeded: UserReducer<FetchUserSucceededPayload> = (state, { payload }) => ({
  ...state,
  list: payload.list,
  advancedList: payload.advancedList,
  isLoading: false,
  trace: null,
});

const handleFetchUsersFailed: UserReducer<FetchUserFailedPayload> = (state, { payload }) => ({
  ...state,
  isLoading: false,
  trace: {
    status: TraceStatus.Error,
    message: payload,
  },
});

const handleSetSearchTerm: UserReducer<SetSearchTermPayload> = (state, { payload }) => ({
  ...state,
  searchTerm: payload,
});

const handleSetList: UserReducer<SetListPayload> = (state, { payload }) => ({
  ...state,
  list: payload.list,
  toggleId: null,
});

const handleToggleUserSucceeded: UserReducer<ToggleUserSucceededPayload> = (state, { payload }) => ({
  ...state,
  toggleId: payload,
});

const handleSetMainContact: UserReducer<SetMainContactPayload> = (state, { payload }) => ({
  ...state,
  loadingUserId: payload,
});

const handleSetMainContactSucceeded: UserReducer = state => ({
  ...state,
  loadingUserId: null,
});

const handleSetMainContactFailed: UserReducer = state => ({
  ...state,
  loadingUserId: null,
});

export default handleActions<UsersState, any>(
  {
    [FETCH_USERS_STARTED]: handleFetchUsers,
    [FETCH_USERS_SUCCEEDED]: handleFetchUsersSucceeded,
    [FETCH_USERS_FAILED]: handleFetchUsersFailed,
    [SET_SEARCH_TERM]: handleSetSearchTerm,
    [SET_LIST]: handleSetList,
    [TOGGLE_USER_SUCCEEDED]: handleToggleUserSucceeded,
    [SET_MAIN_CONTACT]: handleSetMainContact,
    [SET_MAIN_CONTACT_SUCCEEDED]: handleSetMainContactSucceeded,
    [SET_MAIN_CONTACT_FAILED]: handleSetMainContactFailed,
  },
  initialState,
);

// Selectors

export const selectList: SelectorState<User[]> = state => state.users.list;

export const selectAdvancedList: SelectorState<AdvancedUser[]> = state => state.users.advancedList;

export const selectIsLoading: SelectorState<boolean> = state => state.users.isLoading;

export const selectSearchTerm: SelectorState<string> = state => state.users.searchTerm;

export const selectToggleId: SelectorState<Nullable<number>> = state => state.users.toggleId;

export const selectLoadingUserId: SelectorState<Nullable<number>> = state => state.users.loadingUserId;

function filterUsers<T extends Record<string, any>>(list: T[], inputTerm: string, fields: (keyof T)[]): T[] {
  const term = inputTerm ? inputTerm.toLowerCase() : '';

  if (term) {
    return filter(list, user => fields.some(field => includes((String(user[field]) || '').toLowerCase(), term)));
  } else {
    return list;
  }
}

export const getFilteredList: SelectorState<User[]> = state => {
  const list: User[] = selectList(state);
  const inputTerm = selectSearchTerm(state);

  return filterUsers(list, inputTerm, ['first_name', 'last_name', 'email']);
};

export const getFilteredAdvancedList: SelectorState<AdvancedUser[]> = state => {
  const list: AdvancedUser[] = selectAdvancedList(state);
  const inputTerm = selectSearchTerm(state);

  return filterUsers(list, inputTerm, ['full_name', 'email']);
};

export const getUsersCount: SelectorState<number> = state => state.users.list.length;

const rolesCount: DisplayRolesCount = {
  admin: 0,
  editor: 0,
  reader: 0,
  custom: 0,
};

export const getRolesCount: SelectorState<DisplayRolesCount> = state => {
  const list: User[] = selectList(state);

  return { ...rolesCount, ...countBy(list, 'role') };
};
